import { useEffect, useEffectEvent, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

/**
 * The query's data once it has been fetched since `openedKey` was opened, then held until the key
 * changes. For an editor that takes its values once: it must not open on a copy cached before it
 * was opened, and nothing that happens to the query afterwards, a failed background refetch
 * included, may take the copy it opened on away from it.
 *
 * `onFailure` runs once when that first fetch fails, so the caller can tell the user and let go of
 * what it opened. It never runs for a failed refetch after a copy has arrived. A retry starts from
 * a fresh key, so it neither sees the old error nor fires `onFailure` again until it fails itself.
 */
export default function useFetchedSinceOpened<T>(
  query: UseQueryResult<T>,
  openedKey: string | null,
  onFailure?: (error: Error) => void,
): T | undefined {
  const [held, setHeld] = useState<{ key: string | null; data?: T }>({ key: openedKey });

  // isFetchedAfterMount resets when the query key changes, and is set by an error as well as by
  // data, so an error here with nothing held means the first fetch has failed.
  const hasFailed =
    openedKey !== null && held.key === openedKey && query.isFetchedAfterMount && query.isError;
  const failed = hasFailed && held.data === undefined;

  const reportFailure = useEffectEvent(() => {
    if (query.error) onFailure?.(query.error);
  });
  useEffect(() => {
    if (failed) reportFailure();
  }, [failed]);

  if (held.key !== openedKey) {
    setHeld({ key: openedKey });
    return undefined;
  }
  if (held.data === undefined && query.isFetchedAfterMount && !query.isError && query.data) {
    setHeld({ key: openedKey, data: query.data });
    return query.data;
  }
  return held.data;
}
